import _                                                                    from 'underscore';
import React, { CSSProperties, FC, ReactNode, useEffect, useRef, useState } from 'react';
import { FormFieldProps, Input, InputProps }                                from 'semantic-ui-react';
import { useField, useFormikContext }                                       from 'formik';

import { FieldWrapper }              from './FieldWrapper';
import { FieldDescription }          from './FieldDescription';
import { StandaloneFormikMetaProps } from '@src/types';
import { INPUT }                     from '@src/constants';


interface TextFieldProps extends InputProps {
    label?: ReactNode,
    attachedLabel?: InputProps['label'],
    attachedLabelPosition?: InputProps['labelPosition'],
    descriptionText?: ReactNode,
    includeFieldWrapper?: boolean,
    includeErrorMessage?: boolean,
    errorContainerStyles?: CSSProperties,
    autocomplete?: boolean,
    debounce?: boolean,
    debounceValue?: string,
    valuePreprocessor?: ( value: any ) => any,
    debounceTimingMS?: number,
    onDebounceChange?: ( value: any ) => void,

    [ additionalProps: string ]: any,
}

const defaultProps: TextFieldProps = {
    label:                 null,
    attachedLabel:         null,
    attachedLabelPosition: null,
    descriptionText:       null,
    includeFieldWrapper:   true,
    includeErrorMessage:   true,
    errorContainerStyles:  {},
    autocomplete:          false,
    debounce:              false,
    debounceValue:         '',
    valuePreprocessor:     null,
    debounceTimingMS:      INPUT.INPUT_EVENT_DELAY,
    onDebounceChange:      ( value ) => {},
}

// preprocess a value...
const preprocessValue = ( value: any, method: ( v: any ) => any | null ) => {

    return ( _.isFunction( method ) ) ? method( value ) : value;
}

const TextField: FC<TextFieldProps> = ( _props ) => {

    let props: InputProps           = _.omit( _props, _.keys( defaultProps ) ),
        { debounce, debounceValue } = _props;

    // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
    // which we can spread on <input> and also replace ErrorMessage entirely.
    let formik                      = useFormikContext(),
        valueRef                    = useRef( preprocessValue( debounceValue, _props.valuePreprocessor ) ),
        [ timeoutID, setTimeoutID ] = useState<NodeJS.Timeout>( null )

    // clean up any debounced timers
    useEffect( () => {

        return () => {

            if( timeoutID ) clearTimeout( timeoutID );
        }
    }, [] )

    // if the debounced value changes we can reset our ref to that value
    useEffect( () => {

        valueRef.current = debounceValue;

    }, [ debounceValue ] )

    const [ field, meta ] = ( formik ? useField( props.name ) : [ { name: props.name }, {} ] as [ FormFieldProps, StandaloneFormikMetaProps ] );

    let showErrors = ( meta.touched && meta.error ) as boolean,
        Wrapper    = _props.includeFieldWrapper ? FieldWrapper : React.Fragment;

    // pass autocomplete down explicitly
    let htmlInputProps = {
        autoComplete: _props.autocomplete ? 'on' : 'off',
        ...( ( props.input as object ) ?? {} )
    };

    // merge input props, html props, and field props
    // @ts-ignore
    let inputProps: InputProps = {
        ...field,
        ...props,
        label:         _props.attachedLabel ?? null,
        labelPosition: _props.attachedLabelPosition ?? props.labelPosition ?? null,
        input:         htmlInputProps,
    }

    // if behaving like a debounced input...
    if( debounce ){

        // make the input value the currently typed in value from ref
        inputProps[ 'value' ] = valueRef.current;

        // make on change behave like a debounced input.
        // NOTE: debounce change behavior needs to be updated manually.
        // Adapted from https://stackoverflow.com/a/57039707
        inputProps[ 'onChange' ] = ( e: any ) => {

            // save the currently typed-in value
            valueRef.current = preprocessValue( e.target.value, _props.valuePreprocessor );

            // clear any timer running
            if( timeoutID ) clearTimeout( timeoutID );

            // set new timer to perform the change after `debounceTimingMS` milliseconds
            setTimeoutID( setTimeout( () => {

                _props.onDebounceChange( valueRef.current );

            }, _props.debounceTimingMS ) );
        }

    } else {

        inputProps[ 'value' ] = preprocessValue( inputProps[ 'value' ], _props.valuePreprocessor );

    }

    let wrapperProps = {
        showErrors,
        meta,
        field,
        includeErrorMessage:  _props.includeErrorMessage,
        errorContainerStyles: _props.errorContainerStyles
    }

    return (

        <Wrapper { ...wrapperProps }>

            { _props.label &&
              <label htmlFor={ props.id || props.name }>{ _props.label }</label> }

            { _props.descriptionText &&
              <FieldDescription>{ _props.descriptionText }</FieldDescription> }

            <Input { ...inputProps }/>

        </Wrapper>
    );
};

TextField.defaultProps = defaultProps;

export { TextField };
